"use client";

import Cookies from "js-cookie";
import noop from "lodash/noop";
import type { ThemeProviderProps, UseThemeProps } from "next-themes";
import { ThemeProvider as NextThemesProvider, useTheme } from "next-themes";
import { createContext, useContext, useEffect, useState } from "react";

import {
  CUSTOM_COLOR_COOKIE_KEY,
  LOCAL_STORAGE_KEYS,
  THEME_COLORS,
  THEME_COOKIE_KEY,
  THEME_OPTIONS,
} from "@/utils/constants";

const themes: THEME_OPTIONS[] = [
  THEME_OPTIONS.lightMode,
  THEME_OPTIONS.darkMode,
  THEME_OPTIONS.paperLightMode,
  THEME_OPTIONS.paperDarkMode,
  THEME_OPTIONS.modernLightMode,
  THEME_OPTIONS.modernDarkMode,
];

const radiusByTheme: Record<THEME_OPTIONS, number> = {
  [THEME_OPTIONS.lightMode]: 0,
  [THEME_OPTIONS.darkMode]: 0,
  [THEME_OPTIONS.modernDarkMode]: 200,
  [THEME_OPTIONS.modernLightMode]: 200,
  [THEME_OPTIONS.paperLightMode]: 4,
  [THEME_OPTIONS.paperDarkMode]: 4,
};

type OwnThemeContextType = {
  theme: THEME_OPTIONS | undefined;
  darkTheme: boolean;
  themeColor: string | undefined;
  customColor: string | null;
  setCustomColor: (v: string | null) => void;
  setTheme: (v: THEME_OPTIONS) => void;
  themeRadius: number;
} & Partial<UseThemeProps>;

const OwnThemeContext = createContext<OwnThemeContextType>({
  theme: undefined,
  darkTheme: false,
  themeColor: undefined,
  customColor: null,
  setCustomColor: noop,
  setTheme: noop,
  themeRadius: 0,
});

export const useOwnTheme = () => {
  return useContext(OwnThemeContext);
};

type FocusThemeProviderProps = Readonly<ThemeProviderProps> & {
  initialTheme: THEME_OPTIONS;
  initialCustomColor: string | null;
};

const OwnThemeProvider = ({
  children,
  initialTheme,
  initialCustomColor,
}: FocusThemeProviderProps) => {
  const results = useTheme();
  const theme = (results.theme ?? initialTheme) as THEME_OPTIONS;

  const [customColor, setCustomColor] = useState<string | null>(
    initialCustomColor,
  );

  useEffect(() => {
    Cookies.set(THEME_COOKIE_KEY, theme, { expires: 365 });
  }, [theme]);

  useEffect(() => {
    if (customColor) {
      Cookies.set(CUSTOM_COLOR_COOKIE_KEY, customColor, { expires: 365 });
    } else {
      Cookies.remove(CUSTOM_COLOR_COOKIE_KEY);
    }
  }, [customColor]);

  const value = {
    ...results,
    theme,
    darkTheme: theme.includes("dark"),
    themeColor: THEME_COLORS[theme],
    customColor,
    setCustomColor,
    themeRadius: radiusByTheme[theme],
  };

  return (
    <OwnThemeContext.Provider value={value}>
      {children}
      {customColor && (
        <style>
          {`
          :root {
            --primary: ${customColor};
          }
        `}
        </style>
      )}
    </OwnThemeContext.Provider>
  );
};

export function ThemeProvider({
  children,
  initialTheme,
  initialCustomColor,
}: FocusThemeProviderProps) {
  return (
    <NextThemesProvider
      attribute="class"
      defaultTheme={initialTheme}
      storageKey={LOCAL_STORAGE_KEYS.theme}
      themes={themes}
      enableColorScheme={false}
      disableTransitionOnChange
    >
      <OwnThemeProvider
        initialCustomColor={initialCustomColor}
        initialTheme={initialTheme}
      >
        {children}
      </OwnThemeProvider>
    </NextThemesProvider>
  );
}
